import React, { useEffect, useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { createAPIEndPoint } from 'config/api/api';
import toast from 'react-hot-toast';
import { getUserData } from 'utils';

const LocationModal = ({
  isOpen,
  onClose,
  type = 'add',
  locationId,
  fetchAgain,
}) => {
  const userData = getUserData();
  const clinic_id = userData?.clinic_id ?? null;
  const dashboard_id = userData?.dashboard?.id ?? null;
  const role_id = userData?.role?.id ?? null;
  const [loading, setLoading] = useState(false);
  const [locationData, setLocationData] = useState([]);

  const fetchLocation = async () => {
    try {
      const response = await createAPIEndPoint('clinic_locations/').fetchById(
        locationId,
      );
      setLocationData(response.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Error fetching patient');
    }
  };

  useEffect(() => {
    if (locationId && type === 'edit') {
      fetchLocation();
    }
  }, [locationId, isOpen]);

  const handleCreate = async (values, { resetForm }) => {
    try {
      setLoading(true);

      if (locationId) {
        await createAPIEndPoint('clinic_locations/').update(locationId, {
          ...values,
          dashboard_id,
          clinic_id,
        });
      } else {
        await createAPIEndPoint('clinic_locations').create({
          ...values,
          dashboard_id,
          clinic_id,
        });
      }

      toast.success('New location added successfully!');
      setLocationData([]);
      setLoading(false);
      fetchAgain();
      resetForm();
      onClose();
    } catch (error) {
      setLoading(false);
      console.error('Error creating team member:', error);
      const errorMessage =
        error.response?.data?.error ||
        'An error occurred while creating the team member.';
      toast.error(errorMessage);
    }
  };

  const validationSchema = Yup.object().shape({
    location_name: Yup.string().required('Location Name is required'),
    greeting_message: Yup.string().required('Greeting is required'),
    email: Yup.string()
      .email('Invalid email format')
      .required('Email is required'),
    address: Yup.string().required('Address is required'),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required'),
    phone: Yup.string()
      .matches(
        /^\(\d{3}\) \d{3}-\d{4}$/,
        'Phone number must be in format (XXX) XXX-XXXX',
      )
      .required('Phone number is required'),
    postal_code: Yup.string().required('Postal Code is required'),
  });

  const formatPhoneNumber = (value) => {
    const cleaned = value.replace(/\D/g, ''); // Remove non-numeric characters
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);

    if (!match) return value;

    const formatted = !match[2]
      ? match[1]
      : `(${match[1]}) ${match[2]}${match[3] ? `-${match[3]}` : ''}`;

    return formatted.length > 14 ? formatted.slice(0, 14) : formatted; // Limit to 14 characters
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      location_name: locationData?.location_name || '',
      greeting_message: locationData?.greeting_message || '',
      email: locationData?.email || '',
      address: locationData?.address || '',
      map_link: locationData?.map_link || '',
      city: locationData?.city || '',
      state: locationData?.state || '',
      phone: locationData?.phone || '',
      postal_code: locationData?.postal_code || '',
      sip_uri: locationData?.sip_uri || ''
    },
    validationSchema,
    onSubmit: handleCreate,
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        onClose();
        setLocationData([]);
      }}
      isCentered
    >
      <ModalOverlay />
      <ModalContent
        borderRadius="16px"
        pb="16px"
        maxWidth={{ base: '90%', md: '448px' }}
        maxH="90vh"
        overflowY="auto"
      >
        <ModalHeader color="brand.500">
          {type === 'edit' ? 'Edit Location' : 'Add Location'}
        </ModalHeader>
        <ModalCloseButton />
        <form onSubmit={formik.handleSubmit}>
          <ModalBody maxHeight="65vh" overflowY="auto" pb={2}>
            {[
              'location_name',
              'greeting_message',
              'email',
              'address',
              'map_link',
              'city',
              'state',
              'phone',
              'postal_code',
              'sip_uri', 
            ].map((field, index) => (
              <FormControl
                key={index}
                isInvalid={formik.touched[field] && formik.errors[field]}
                mb={2}
              >
                <FormLabel mb={0} textTransform="capitalize">
                  {field
                    .replace(/_/g, ' ') // Replace underscores with spaces
                    .replace(/([A-Z])/g, ' $1') // Add space before capital letters
                    .trim() // Remove any leading or trailing spaces
                  }                </FormLabel>
                <Input
                  name={field}
                  placeholder={`Enter ${field
                    .replace(/_/g, ' ')
                    .replace(/([A-Z])/g, ' $1')
                    .trim()
                    .toLowerCase()}`}
                  variant="auth"
                  fontSize="sm"
                  fontWeight="500"
                  maxLength={field === 'phone' ? 14 : undefined} // Limit phone input length
                  value={formik.values[field]}
                  onChange={(e) =>
                    field === 'phone'
                      ? formik.handleChange({
                        target: {
                          name: 'phone',
                          value: formatPhoneNumber(e.target.value),
                        },
                      })
                      : formik.handleChange(e)
                  }
                  onBlur={formik.handleBlur}
                />
                {formik.touched[field] && formik.errors[field] && (
                  <Text color="red.500" fontSize="sm">
                    {formik.errors[field]}
                  </Text>
                )}
              </FormControl>
            ))}


          </ModalBody>
          <ModalFooter pt={0}>
            <Button type="submit" colorScheme="brand" w="100%" mt={4} isLoading={formik.isSubmitting}>
              {type === 'edit' ? 'Update Location' : 'Add Location'}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default LocationModal;
